import { useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { SuggestedActionChips } from '../blocks/SuggestedActionChips';
import {
  useSuggestedActionsEntries,
  type FloatingActionEntry,
} from './floatingActionsContext';

const THREAD_SCROLL_SELECTOR = '[role="log"]';

// The output being read: the first row not scrolled past, and whether it's
// still below the fold. Measured directly — an IntersectionObserver rooted at
// the thread scroll region delivered no callbacks here.
const readCurrentRow = (
  entries: FloatingActionEntry[]
): { entry: FloatingActionEntry; isBelow: boolean } | null => {
  const root = entries[0]?.node?.closest(THREAD_SCROLL_SELECTOR);
  if (!root) return null;
  const rootRect = root.getBoundingClientRect();
  for (const entry of entries) {
    if (!entry.node) continue;
    const rect = entry.node.getBoundingClientRect();
    if (rect.bottom <= rootRect.top) continue; // scrolled past
    return { entry, isBelow: rect.top >= rootRect.bottom };
  }
  return null;
};

// Floats the current output's suggested actions above the composer, but only
// while that output's inline row is still below the fold — so it never
// duplicates a row you can already see, and never shows at the bottom.
export const FloatingSuggestedActions = ({
  active,
}: {
  // True when scrolled away from the bottom (same check as the jump pill).
  active: boolean;
}) => {
  const entries = useSuggestedActionsEntries();
  // Bumped on scroll to re-measure.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const root = entries[0]?.node?.closest(THREAD_SCROLL_SELECTOR);
    if (!root) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setTick((t) => t + 1));
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      root.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [entries]);

  const current = useMemo(
    () => readCurrentRow(entries),
    // `tick` re-measures after each scroll frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, tick]
  );

  if (!active || !current || !current.isBelow) return null;

  return (
    <Box
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="12px"
      boxShadow="0 4px 16px rgba(38, 34, 68, 0.12)"
      px="10px"
      py="8px"
      maxW="100%"
      overflowX="auto"
      data-test-id="agentforge-floating-suggested-actions"
    >
      <SuggestedActionChips
        actions={current.entry.actions}
        onSelect={current.entry.onSelect}
        disabled={current.entry.disabled}
        testIdPrefix={`${current.entry.testIdPrefix}-floating`}
        title={null}
        register={false}
      />
    </Box>
  );
};
